import React, { useRef, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MotifPagination } from '../pagination/pagination';

import { AgGridReact } from 'ag-grid-react';

/**
 * Returns object with heights to apply on header and regular rows. Expect a boolean as parameter to return compact table heights.
 * @param {Boolean} isCompact - TRUE to return heighst for a 36px row table
 * @returns {Object} - {headerHeight, rowHeight}
 */
export const getRowsHeight = isCompact => {
  if (isCompact) {
    return { headerHeight: 43, rowHeight: 36 };
  }

  return { headerHeight: 43, rowHeight: 44 };
};

const getMaxPage = (dataLength, pageSize) =>
  Math.floor(dataLength / Number(pageSize)) + (dataLength % Number(pageSize) ? 1 : 0);

/**
 * @typedef Props
 * @prop {React.ReactNode} children
 * @prop {string} [className]
 * @prop {string} [title]
 * @prop {{}} [localeText]
 * @prop {boolean} [firstColumnBorderRight]
 * @prop {boolean} [compact]
 * @prop {React.ReactNode} [paginationSelect]
 * @prop {{}[]} [rowData]
 * @prop {number | string} [paginationPageSize]
 * @prop {boolean} [pagination]
 * @prop {() => void} [onFilterChanged]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifTable = props => {
  const {
    className = '',
    title = '',
    children,
    localeText = null,
    firstColumnBorderRight = false,
    compact = false,
    paginationSelect = null,
    rowData = [],
    paginationPageSize = 4,
    pagination = false,
    onFilterChanged = null,
    ...other
  } = props;
  const gridRef = useRef();
  const [currentPage, setCurrentPage] = useState(1);
  const [maxPage, setMaxPage] = useState(() => getMaxPage(rowData.length, paginationPageSize));
  const { headerHeight, rowHeight } = getRowsHeight(compact);

  useEffect(() => {
    setMaxPage(getMaxPage(gridRef.current.api.getDisplayedRowCount(), paginationPageSize));
  }, [paginationPageSize, rowData]);

  const handlePageChange = val => {
    setCurrentPage(val);
    gridRef.current.api.paginationGoToPage(val - 1);
  };

  const handleOnFilter = () => {
    setMaxPage(getMaxPage(gridRef.current.api.getDisplayedRowCount(), paginationPageSize));
    if (onFilterChanged) {
      onFilterChanged();
    }
  };

  const topClasses = classNames({
    'motif-table': true,
    'ag-theme-material': true,
    [className]: true
  });

  const classes = classNames({
    'motif-table-wrapper': true,
    'motif-table-first-column-border': firstColumnBorderRight,
    'motif-table-has-title': title
  });

  const newLocaleText = {
    page: ' ',
    of: '/',
    ariaRowSelectAll: 'Select all checkbox',
    ...localeText
  };

  return (
    <div className={topClasses} data-motif-table>
      {title && <div className="motif-table-title-bar">{title}</div>}
      {children}
      <div className={classes}>
        <AgGridReact
          ref={gridRef}
          rowHeight={rowHeight}
          headerHeight={headerHeight}
          localeText={newLocaleText}
          paginationPageSize={Number(paginationPageSize)}
          suppressPaginationPanel
          rowData={rowData}
          onFilterChanged={handleOnFilter}
          {...other}
        />
        <div className="pagination-control-wrapper">
          {paginationSelect}
          {pagination && (
            <MotifPagination
              onPageChange={handlePageChange}
              currentPage={currentPage > maxPage ? maxPage : currentPage}
              min={1}
              max={maxPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

MotifTable.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  className: PropTypes.string,
  localeText: PropTypes.objectOf(PropTypes.object),
  firstColumnBorderRight: PropTypes.bool,
  compact: PropTypes.bool,
  paginationSelect: PropTypes.node,
  rowData: PropTypes.arrayOf(PropTypes.object),
  paginationPageSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pagination: PropTypes.bool,
  onFilterChanged: PropTypes.func
};

export { MotifTable };
